import { useUserInfo } from "@/api/user";
import { useManageModalConfig } from "@/components/ManageModal/manageConfig";
import { ROUTES } from "@/constants/routes";
import type { OpenSettingsSource } from "@/context/OpenSettingsContext";
import type { OpenSettingsInput } from "@/utils/settingsNavigation";
import { isPermittedSettingsDeepLink } from "@/utils/settingsNavigation";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Opens the Manage modal when the URL is under `/settings/*`, and returns the
 * browser to where it was when the user closes it. Settings URLs are deep
 * links, not pages: nothing renders behind them, so closing has to land
 * somewhere — the last non-settings location the app was on, or Home.
 */
/** Routes that exist only to redirect into the modal — never a place to return to. */
const REDIRECTS_INTO_SETTINGS = new Set<string>([
  ROUTES.ARCHIVE.ROOT,
  ROUTES.ARCHIVE.LEGACY_TRASH,
]);

export function useSettingsRouteSync(
  openSettings: (
    input?: OpenSettingsInput,
    source?: OpenSettingsSource,
  ) => void,
  settingsDialogOpen: boolean,
  closeSettings: () => void,
) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading: isUserInfoLoading } = useUserInfo();
  const { allMenuItems } = useManageModalConfig();
  const lastSyncedPathRef = useRef<string | null>(null);
  const wasDialogOpenRef = useRef(false);
  const lastNonSettingsPathRef = useRef<string | null>(null);
  const previousPathRef = useRef<string | null>(null);

  // Open the dialog when the browser lands on (or navigates to) a settings URL.
  useEffect(() => {
    if (!location.pathname.startsWith(ROUTES.SETTINGS.ROOT)) {
      lastSyncedPathRef.current = null;
      // Remembering a path that only redirects back into the modal would make
      // closing it an infinite loop.
      if (!REDIRECTS_INTO_SETTINGS.has(location.pathname)) {
        lastNonSettingsPathRef.current = `${location.pathname}${location.search}`;
      }
      return;
    }

    if (isUserInfoLoading) {
      return;
    }

    if (lastSyncedPathRef.current === location.pathname) {
      return;
    }

    lastSyncedPathRef.current = location.pathname;

    if (!isPermittedSettingsDeepLink(location.pathname, allMenuItems)) {
      return;
    }

    openSettings(location.pathname, "deep_link");
  }, [
    location.pathname,
    location.search,
    openSettings,
    isUserInfoLoading,
    allMenuItems,
  ]);

  // The browser navigated OFF a settings URL (Back, or an in-app link) — the
  // modal goes with it. Keyed on the transition, never on the current path:
  // `openSettings()` deliberately doesn't write a URL, so an open modal
  // sitting at "/" is the normal case, not a reason to close.
  useEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = location.pathname;

    const leftSettingsUrl =
      previousPath !== null &&
      previousPath.startsWith(ROUTES.SETTINGS.ROOT) &&
      !location.pathname.startsWith(ROUTES.SETTINGS.ROOT);

    if (leftSettingsUrl && settingsDialogOpen) {
      closeSettings();
    }
  }, [location.pathname, settingsDialogOpen, closeSettings]);

  // Leave settings URLs only after the user closes an open dialog — not on
  // mount while the open effect above is still pending.
  useEffect(() => {
    if (
      wasDialogOpenRef.current &&
      !settingsDialogOpen &&
      location.pathname.startsWith(ROUTES.SETTINGS.ROOT)
    ) {
      navigate(lastNonSettingsPathRef.current ?? ROUTES.HOME, {
        replace: true,
      });
    }
    wasDialogOpenRef.current = settingsDialogOpen;
  }, [settingsDialogOpen, location.pathname, navigate]);
}
